'use client'

import { hasVietnamese, stringToSlug } from '@/lib/utils'
import debounce from 'lodash.debounce'
import { useEffect } from 'react'
import { useIsMobileClient } from '../common/use-device-type'

interface GATrackingEvent {
  event_category: string
  event_label: string
  event_value: number
  link_class: string
  transport_type: string
}

const gtagClickTracking = (value: string, label: string, isMobile: boolean) => {
  console.info(
    'click_tracking',
    `${isMobile ? 'Mb_' : ''}${value}`,
    label,
    isMobile
  )

  // Safely check if window and gtag exist
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    try {
      const trackingEvent: GATrackingEvent = {
        event_category: 'click_tracking',
        event_label: label,
        event_value: 1,
        link_class: `${isMobile ? 'Mb_' : ''}${value}`,
        transport_type: 'beacon',
      }

      window.gtag('event', 'click_tracking', trackingEvent)
    } catch (error) {
      console.error('Error calling gtag:', error)
    }
  }
}

export function useGATracking(useGa: boolean) {
  const isMobile = useIsMobileClient()
  useEffect(() => {
    if (!useGa) return
    const handleClick = debounce((e: MouseEvent) => {
      const target = e.target as HTMLElement
      const trackingElement = target.closest(
        'button, a, [data-ga-tracking-value]'
      )
      if (!(trackingElement instanceof HTMLElement)) return

      const tag = trackingElement.tagName.toLowerCase()
      if (!['button', 'a'].includes(tag)) return

      const trackingLevels: { level: number; value: string }[] = []

      let currentElement: HTMLElement | null = trackingElement
      while (currentElement) {
        const levelAttrs = Array.from(currentElement.attributes)
          .filter((attr) => attr.name.startsWith('data-tracking-level-'))
          .map((attr) => {
            const match = attr.name.match(/data-tracking-level-(\d+)/)
            return match ? { level: Number(match[1]), value: attr.value } : null
          })
          .filter(Boolean) as { level: number; value: string }[]

        trackingLevels.push(...levelAttrs)
        currentElement = currentElement.parentElement
      }

      trackingLevels.sort((a, b) => a.level - b.level)

      let value = trackingLevels
        .map((t) => (hasVietnamese(t.value) ? stringToSlug(t.value) : t.value))
        .join('_')

      if (!value) {
        value = trackingElement.getAttribute('data-ga-tracking-value') || tag
      }

      if (tag === 'button') {
        const parts = value.split('_')
        parts.splice(-1, 0, 'CTA')
        value = parts.join('_')
      }

      const label =
        trackingElement.getAttribute('data-ga-tracking-label') ||
        trackingElement.textContent?.trim()

      if (value && label) {
        gtagClickTracking(value, label, isMobile)
      }
    }, 500)

    document.addEventListener('click', handleClick)
    return () => {
      document.removeEventListener('click', handleClick)
    }
  }, [isMobile, useGa])
}
